import React, { useEffect } from 'react'
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { PageLoader } from '@/components/ui/LoadingSpinner'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'

// Layouts
import AuthLayout from '@/components/layouts/AuthLayout'
import DashboardLayout from '@/components/layouts/DashboardLayout'

// Auth Pages
import LoginPage from '@/pages/auth/LoginPage'
import RegisterPage from '@/pages/auth/RegisterPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import VerifyOtpPage from '@/pages/auth/VerifyOtpPage'
import CallbackPage from '@/pages/auth/CallbackPage'
import AcceptInvitePage from '@/pages/team/AcceptInvitePage'

// Legal Pages — reachable signed-in or signed-out (linked from the register form)
import TermsPage from '@/pages/legal/TermsPage'
import AdminApp from '@/admin/AdminApp'
import PrivacyPage from '@/pages/legal/PrivacyPage'

// Dashboard Pages
import DashboardPage from '@/pages/dashboard/DashboardPage'
import WorkflowsPage from '@/pages/workflows/WorkflowsPage'
import WorkflowBuilderPage from '@/pages/workflows/WorkflowBuilderPage'
import MaterialsPage from '@/pages/materials/MaterialsPage'
import ContactsPage from '@/pages/contacts/ContactsPage'
import CampaignsPage from '@/pages/campaigns/CampaignsPage'
import CampaignDetailPage from '@/pages/campaigns/CampaignDetailPage'
import AnalyticsPage from '@/pages/analytics/AnalyticsPage'
import SettingsPage from '@/pages/settings/SettingsPage'
import SenderEmailsPage from '@/pages/sender-emails/SenderEmailsPage'
import InboxPage from '@/pages/inbox/InboxPage'
import TasksPage from '@/pages/tasks/TasksPage'
import BillingPage from './pages/billing/BillingPage'
// import LinkedInCallbackPage from './pages/settings/LinkedInCallbackPage'  // shelved with the LinkedIn provider
import TemplatesPage from './pages/templates/TemplatesPage'
import TemplateEditorPage from './pages/templates/TemplateEditorPage'

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuthStore()
  const location = useLocation()

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}

// Public Route Component (redirect if authenticated)
const PublicRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuthStore()
  const location = useLocation()

  if (isAuthenticated) {
    const from = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/dashboard'
    return <Navigate to={from} replace />
  }

  return <>{children}</>
}

function App() {
  const { isAuthenticated, refreshUser } = useAuthStore()
  const [isInitialized, setIsInitialized] = React.useState(false)

  useEffect(() => {
    // Re-validate session in the background. The persisted Zustand state already
    // tells us whether the user is authenticated; `refreshUser` runs as a side-effect
    // to refresh the profile and, if the token is expired, lets the axios interceptor
    // redirect to /login. We intentionally do NOT await it — otherwise an unreachable
    // backend hangs the splash for the full axios timeout (120s) and the app gets
    // stuck on "Initializing…".
    refreshUser()
    setIsInitialized(true)
  }, [refreshUser])

  if (!isInitialized) {
    return <PageLoader text="Initializing..." />
  }

  return (
    <ErrorBoundary>
    <div className="min-h-[100dvh] bg-background">
      <Routes>
        {/* ------------------------------------------------------------------
            Public routes — AuthLayout is hoisted to a LAYOUT ROUTE so it is
            NOT remounted when moving between /login, /register, … Nesting it
            inside each route element replayed the whole entrance animation
            (marketing panel, ampersand, hairline) on every navigation.
        ------------------------------------------------------------------ */}
        <Route
          element={
            <PublicRoute>
              <AuthLayout />
            </PublicRoute>
          }
        >
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-otp" element={<VerifyOtpPage />} />
        </Route>

        {/* WorkOS SSO landing — receives minted JWTs from the backend redirect.
            No AuthLayout: it's a transient full-screen spinner that self-navigates. */}
        <Route
          path="/auth/callback"
          element={
            <PublicRoute>
              <CallbackPage />
            </PublicRoute>
          }
        />

        {/* Admin panel (ADMIN_FLOW.md): team-only, its own login, token and
            layout. Outside PublicRoute/ProtectedRoute on purpose — a tenant
            session must neither grant nor be granted anything here. */}
        <Route path="/admin/*" element={<AdminApp />} />

        {/* Legal pages — deliberately NOT wrapped in PublicRoute (which bounces
            authenticated users to /dashboard) or ProtectedRoute. They must be
            readable in both states, e.g. from the register form's consent line. */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* Invite acceptance: same reasoning as the legal pages above. An
            already-authenticated visitor (signed into a DIFFERENT account in
            this browser) must still be able to see and accept an invite, since
            accepting can merge their existing solo account into the inviting
            one. PublicRoute would bounce them to /dashboard before they ever
            saw it. Wrapped in AuthLayout directly (not via the layout-route
            group above) so it gets the same branded chrome without inheriting
            PublicRoute's redirect-if-authenticated behavior. */}
        <Route
          path="/team/accept-invite"
          element={
            <AuthLayout>
              <AcceptInvitePage />
            </AuthLayout>
          }
        />

        {/* ------------------------------------------------------------------
            Protected routes inside the app shell — DashboardLayout is a LAYOUT
            ROUTE, so it stays mounted across navigations. Previously it lived
            inside every route element, which remounted it on each page change:
            notification polling restarted and UpgradeCard re-flashed its
            skeleton every time. The layout renders one <ErrorBoundary> around
            its <Outlet>, keyed on pathname, so per-route boundaries are no
            longer needed here.
        ------------------------------------------------------------------ */}
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/workflows" element={<WorkflowsPage />} />
          <Route path="/materials" element={<MaterialsPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
          <Route path="/sender-emails" element={<SenderEmailsPage />} />
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/billing/estimator" element={<BillingPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/templates/new" element={<TemplateEditorPage />} />
          <Route path="/templates/:id/edit" element={<TemplateEditorPage />} />
        </Route>

        {/* Protected, but chrome-less — these own the full viewport (the workflow
            canvas) or are transient OAuth landings. They stay outside the shell. */}
        <Route
          path="/workflows/builder"
          element={
            <ProtectedRoute>
              <WorkflowBuilderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workflows/builder/:id"
          element={
            <ProtectedRoute>
              <WorkflowBuilderPage />
            </ProtectedRoute>
          }
        />
        {/* LinkedIn OAuth callback - DISABLED / SHELVED (2026-08-28). The
            provider was removed from the Integrations page; the page component
            is still on disk (pages/settings/LinkedInCallbackPage.tsx) so this
            route can be restored. See the checklist in
            backend/app/services/linkedin_service.py. */}
        {/*
        <Route
          path="/settings/linkedin/callback"
          element={
            <ProtectedRoute>
              <LinkedInCallbackPage />
            </ProtectedRoute>
          }
        />
        */}

        {/* Default Routes */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* 404 Route — §24 empty state. Two exits on purpose: history.back() is a
            no-op in a fresh tab (pasted/bookmarked bad URL), so a single "Go back"
            button left the user at a dead end. */}
        <Route
          path="*"
          element={
            <div className="min-h-[100dvh] flex items-center justify-center px-6">
              <div className="text-center max-w-md">
                <p
                  className="text-[#8a8f98] dark:text-[#62666d] mb-3"
                  style={{
                    fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
                    fontSize: '44px',
                    fontWeight: 400,
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  404
                </p>
                <h1
                  className="text-foreground dark:text-[#f7f8f8] mb-2"
                  style={{ fontSize: '20px', fontWeight: 590, lineHeight: 1.33, letterSpacing: '-0.24px' }}
                >
                  Page not found
                </h1>
                <p
                  className="text-muted-foreground dark:text-[#8a8f98] mb-6 leading-relaxed"
                  style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                >
                  The page you're looking for doesn't exist, or it may have moved.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => window.history.back()}
                    className="inline-flex items-center justify-center h-10 px-4 rounded-md bg-[#5e6ad2] text-white hover:bg-[#7170ff] active:bg-[#5058c0] transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5e6ad2]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-[#0f1011]"
                    style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
                  >
                    Go back
                  </button>
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-border/60 bg-background text-foreground hover:bg-muted/50 hover:border-border/80 dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#e2e4e7] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.14] transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5e6ad2]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-[#0f1011]"
                    style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
                  >
                    Go to dashboard
                  </Link>
                </div>
              </div>
            </div>
          }
        />
      </Routes>
    </div>
    </ErrorBoundary>
  )
}

export default App
